export const stripTrailingSlash = (str) => {
  return str.endsWith('/') ? str.slice(0, -1) : str;
};
export const padNumber = (num) => {
  if (typeof num !== 'number') return '';
  return num.toString(10).padStart(2, '0');
};

/**
 * remove craft-convention for home-uri "__home__" from home-entry uri
 * @param {string} uri
 * @returns string
 */
export const sanitizeHomeUri = (uri) => (uri === '__home__' ? '' : uri);

/**
 * test for html element
 * @param {*} element
 * @returns boolean
 */
export const isElement = (element) => element instanceof Element || element instanceof HTMLDocument;

export const scrollToSelectorRespectingBars = (selector, additionalOffset = 0) => {
  let target = null;

  try {
    target = document.querySelector(selector);
  } catch (e) {
    return;
  }

  if (!target) return;

  const chapterNavBarHeight = document.querySelector('#ChapterNavBar')?.getBoundingClientRect()?.height ?? 0;
  const pageHeaderHeight = document.querySelector('#PageHeader')?.getBoundingClientRect()?.height ?? 0;

  const offset =
    target.getBoundingClientRect().top +
    window.pageYOffset +
    additionalOffset -
    (chapterNavBarHeight + pageHeaderHeight);

  window.scrollTo({ top: offset, behavior: 'smooth' });
};

// Function to remove HTML Tags from a string
export const stripHtml = (string) => {
  if (typeof string === 'string') {
    return string.replace(/(<([^>]+)>)/gi, '').replaceAll('\n', '');
  }

  return '';
};

export const normalizeTypeHandles = (blocks) => {
  blocks.forEach((block) => {
    block.typeHandle = block.typeHandle.replace(/\d+$/g, '').trim();
  });

  return blocks;
};

export function getFocusableElements(wrapper) {
  return Array.from(
    wrapper.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
  ).filter((el) => !el.disabled && getComputedStyle(el).display !== 'none');
}

/**
 * Clean and truncate description for SEO
 * @param {string} desc - Description to clean
 * @param {number} maxLength - Maximum length of cleaned description (default: 200)
 * @returns {string} Cleaned description
 */
export const cleanDescription = (desc, maxLength = 200) => {
  if (!desc) return '';
  let clean = desc
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (clean.length > maxLength) {
    clean = clean.substring(0, maxLength - 3) + '...';
  }
  return clean;
};

export const extractSentences = (desc, limit = 5) => {
  return desc
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .split(/[.!?]+/) // Split into sentences
    .filter((sentence) => sentence.trim().length > 0) // Remove empty sentences
    .slice(0, limit); // set limit of sentences
};

/**
 * Format dates to ISO 8601 format
 * @param {string} dateString - Date string to format
 * @returns {string|null} ISO 8601 formatted date or null if invalid
 */
export const formatDate = (dateString) => {
  if (!dateString) return null;
  try {
    return new Date(dateString).toISOString();
  } catch (e) {
    console.warn('[formatDate] Error formatting date:', dateString, e);
    return null;
  }
};

/**
 * Get current date in ISO 8601 format
 * @returns {string} Current date in ISO format
 */
export const getCurrentISODate = () => {
  return new Date().toISOString();
};

/**
 * Parse seomatic metaTagContainer for description
 * @param {object} seomatic - Seomatic object containing metaTagContainer
 * @param {string} fallback - Fallback description if parsing fails
 * @returns {string} Parsed description or fallback
 */
export const parseSeomaticDescription = (seomatic, fallback = '') => {
  if (!seomatic?.metaTagContainer) return fallback;

  try {
    const metas = JSON.parse(seomatic.metaTagContainer);
    // Primary description key
    let descEntry = metas.description;
    // Fallback: search for meta tag with name 'description'
    if (!descEntry) {
      descEntry = Object.values(metas).find((m) => !Array.isArray(m) && m.name === 'description');
    }
    if (Array.isArray(descEntry) && descEntry.length > 0) {
      return descEntry[0].content;
    } else if (descEntry?.content) {
      return descEntry.content;
    }
  } catch (e) {
    console.warn('[parseSeomaticDescription] Error parsing seomatic.metaTagContainer', e);
  }

  return fallback;
};

/**
 * Map locale to path
 * @param {string} locale - Locale code
 * @returns {string} Locale path
 */
export const getLocalePath = (locale) => {
  const localePathMap = {
    de: '/de',
    en: '/en',
    fr: '/fr',
    nl: '/nl',
    'ch-de': '/ch-de',
    'ch-fr': '/ch-fr',
  };
  return localePathMap[locale] || '/en'; // Default to English if locale not found
};

/**
 * Map locale to area served
 * @param {string} locale - Locale code
 * @returns {string} Area served
 */
export const getAreaServed = (locale) => {
  const areaMap = {
    de: 'Germany',
    en: 'Worldwide',
    fr: 'France',
    nl: 'Netherlands',
    'ch-de': 'Switzerland',
    'ch-fr': 'Switzerland',
  };
  return areaMap[locale] || 'Worldwide';
};

/**
 * Generate page ID from base URL and URI
 * @param {string} uri - Page URI
 * @param {function} localeRootPath - Locale root path function
 * @param {string} baseUrl - Base URL (defaults to https://blackroll.com)
 * @returns {string} Full page ID
 */
export const generatePageId = (uri, localeRootPath, baseUrl = 'https://blackroll.com') => {
  return `${baseUrl}${localeRootPath('/' + uri)}`;
};

/**
 * Extract text content from content blocks
 * @param {Array} contentBlocks - Array of content blocks
 * @param {number} maxLength - Maximum length of extracted text
 * @returns {string} Extracted and cleaned text content
 */
export const extractContentBlocksText = (contentBlocks, maxLength = 500) => {
  if (!contentBlocks?.length) return '';

  const contentText = contentBlocks
    .map((block) => block.text || block.content || '')
    .filter((text) => text.trim())
    .join(' ')
    .substring(0, maxLength);

  return contentText;
};

/**
 * Generate HowTo steps from sentences
 * @param {Array<string>} sentences - Array of sentence strings
 * @param {string|null} pngUrl - Optional image URL for steps
 * @param {string|null} videoYoutubeId - Optional YouTube video ID for steps
 * @returns {Array<Object>} Array of HowToStep objects
 */
export const generateHowToSteps = (sentences, pngUrl = null, videoYoutubeId = null) => {
  const { t } = useI18n();
  return sentences.map((sentence, index) => ({
    '@type': 'HowToStep',
    name: `${t('exercises.step')} ${index + 1}: ${sentence.trim().substring(0, 50)}...`,
    text: sentence.trim(),
    position: index + 1,
    ...(pngUrl && {
      image: {
        '@type': 'ImageObject',
        url: pngUrl,
        description: `${t('exercises.step')} ${index + 1}`,
      },
    }),
    ...(videoYoutubeId && {
      video: {
        '@type': 'VideoObject',
        url: `https://www.youtube.com/watch?v=${videoYoutubeId}`,
        name: `${t('exercises.step')} ${index + 1}`,
        description: sentence.trim(),
      },
    }),
  }));
};

/**
 * Generate HowTo tools from products
 * @param {Array<Object>} products - Array of product objects
 * @param {string} baseUrl - Base URL for product links
 * @param {Function} localeRootPath - Locale root path function
 * @returns {Array<Object>|null} Array of HowToTool objects or null if no products
 */
export const generateHowToTools = (products, baseUrl, localeRootPath) => {
  if (!products || !Array.isArray(products) || products.length === 0) return null;
  return products
    .filter((product) => product && product.title && product.handle)
    .map((product) => ({
      '@type': 'HowToTool',
      name: product.title,
      url: `${baseUrl}${localeRootPath('/products/' + product.handle)}`,
    }));
};
